/**
 * Lightweight, dependency-free fuzzy matching for restaurant search.
 * Plain String.includes only matches a verbatim contiguous run, so "cafe bistro"
 * fails against "Café, Bistro". These helpers normalize both sides and match per
 * word so search forgives accents, punctuation and word order, with optional
 * typo tolerance via per-token edit distance.
 */

export function normalizeForSearch(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip combining diacritics
    .replace(/[^\p{L}\p{N}]+/gu, " ") // non letter/number -> space
    .trim();
}

function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let curr = new Array<number>(b.length + 1);
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[b.length];
}

function tokenMatches(textWords: string[], token: string): boolean {
  const allowedEdits = token.length >= 5 ? 2 : token.length >= 3 ? 1 : 0;
  return textWords.some((word) => {
    if (word.includes(token)) return true;
    if (allowedEdits === 0) return false;
    const candidate =
      word.length > token.length
        ? word.slice(0, token.length + allowedEdits)
        : word;
    return editDistance(candidate, token) <= allowedEdits;
  });
}

export function fuzzyMatch(
  text: string | null | undefined,
  query: string,
): boolean {
  const normalizedQuery = normalizeForSearch(query ?? "");
  if (!normalizedQuery) return true;
  const normalizedText = normalizeForSearch(text ?? "");
  if (!normalizedText) return false;
  if (normalizedText.includes(normalizedQuery)) return true;
  const textWords = normalizedText.split(" ").filter(Boolean);
  const queryTokens = normalizedQuery.split(" ").filter(Boolean);
  return queryTokens.every((token) => tokenMatches(textWords, token));
}
